import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Polyline, Rectangle, useMap } from 'react-leaflet';

// Same lineWidth → leaflet weight mapping MapComponent uses, so the Basic-tab
// preview matches the dashboard's "map" view exactly. Kept inline rather than
// imported to avoid touching MapComponent for this UX-only change.
const resolveWeight = (lineWidth) => {
  if (typeof lineWidth === 'number') return lineWidth;
  if (lineWidth === 'very thin') return 0.5;
  if (lineWidth === 'thin') return 0.75;
  if (lineWidth === 'thick') return 3;
  if (lineWidth === 'very thick') return 5;
  return 0.8;
};

// Imperative bounds-fit: react-leaflet's <MapContainer bounds={...} /> only
// honours the prop on initial mount. The popup re-renders when the user picks
// a different style or when rectangleBounds changes upstream, so we re-fit
// imperatively to keep WYSIWYG with the print rectangle.
//
// `paddingPx` controls how much extra map sits around the print rectangle —
// more padding means the rectangle outline is clearly inset rather than
// pressed against the viewport edges. The big preview wants visible
// padding; thumbnails want as much as possible to fill the tile.
const FitToBounds = ({ bounds, paddingPx = 4 }) => {
  const map = useMap();
  useEffect(() => {
    if (!bounds) return;
    try {
      // Wait a tick for the leaflet container to settle in the DOM, otherwise
      // fitBounds can compute zoom against zero-size container.
      const id = setTimeout(() => {
        map.invalidateSize();
        map.fitBounds(bounds, { padding: [paddingPx, paddingPx], animate: false });
      }, 0);
      return () => clearTimeout(id);
    } catch (e) {
      // Bounds may briefly be malformed during settings restoration.
    }
  }, [bounds, map, paddingPx]);
  return null;
};

/**
 * Locked-down preview of a Basic basemap style.
 *
 * - No interaction (no zoom, drag, click, keyboard, scroll-wheel).
 * - Fixed to `bounds` (the user's print rectangle). What you see is what
 *   the backend will render.
 * - Optional `polylines` are drawn on top using the user's activity color
 *   and opacity, so the user can judge contrast against each basemap.
 * - `compact` mode trims attribution + zoom controls so thumbnails stay
 *   clean. The big preview keeps attribution (legally required).
 */
const StylePreview = ({
  style,
  bounds,
  // Activity objects shaped like MapComponent's: `{ id, type, coordinates }`.
  // Each gets its own color (per-type if `byTypeEnabled`, else the common
  // color), matching how the dashboard map looks in real time.
  activities,
  activitySettings,
  height,
  compact = false,
}) => {
  const safeBounds = useMemo(() => {
    if (!bounds) return null;
    // Accept either Leaflet [[lat, lng], [lat, lng]] form or a backend
    // (min_lon, min_lat, max_lon, max_lat) tuple.
    if (Array.isArray(bounds) && bounds.length === 2 && Array.isArray(bounds[0])) {
      return bounds;
    }
    if (Array.isArray(bounds) && bounds.length === 4) {
      const [minLon, minLat, maxLon, maxLat] = bounds;
      return [[minLat, minLon], [maxLat, maxLon]];
    }
    return null;
  }, [bounds]);

  // Center fallback if no bounds supplied yet — center on Europe so the
  // basemap renders something rather than going gray.
  const fallbackCenter = [50.0, 10.0];
  const fallbackZoom = 4;

  const commonOpacity = typeof activitySettings?.alpha === 'number'
    ? activitySettings.alpha
    : 0.8;
  const commonColor = activitySettings?.color || 'sienna';
  const byTypeEnabled = !!activitySettings?.byTypeEnabled;
  const byType = activitySettings?.byType || {};
  // Resolve the per-polyline color/opacity. Only the big Basic-tab preview
  // honors per-type overrides — compact thumbnails stay calm with the common
  // color so the basemap's character reads at thumbnail size.
  const resolveLineColor = (type) => {
    if (compact || !byTypeEnabled) return commonColor;
    const override = byType[type]?.color;
    return override || commonColor;
  };
  const resolveLineOpacity = (type) => {
    if (compact || !byTypeEnabled) return commonOpacity;
    const override = byType[type]?.alpha;
    return typeof override === 'number' ? override : commonOpacity;
  };
  // Compact thumbnails get a thinner line so the basemap's character is still
  // visible; the big preview uses the user's actual chosen weight.
  const baseWeight = resolveWeight(activitySettings?.lineWidth);
  const lineWeight = compact ? Math.max(0.6, baseWeight * 0.6) : baseWeight;

  if (!style) return null;

  const containerStyle = {
    width: '100%',
    // Default is 100% — the parent (aspect-[4/3] tile, sized container,
    // etc.) is expected to define the height. Pass a numeric `height`
    // prop only for callers that don't supply that constraint.
    height: typeof height === 'number' ? `${height}px` : '100%',
    pointerEvents: 'none',
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: containerStyle.height }}>
      <MapContainer
        bounds={safeBounds || undefined}
        center={safeBounds ? undefined : fallbackCenter}
        zoom={safeBounds ? undefined : fallbackZoom}
        zoomControl={false}
        attributionControl={!compact}
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        touchZoom={false}
        boxZoom={false}
        keyboard={false}
        style={containerStyle}
      >
        <TileLayer
          url={style.url}
          attribution={style.attribution}
          subdomains={style.subdomains || 'abc'}
          maxZoom={style.maxZoom || 19}
        />
        {/* Big Basic-tab preview honors `byTypeEnabled` so the user can see
            per-type colors/opacity update live as they edit. Thumbnails and
            the Map tab keep using only the common color/alpha. */}
        {Array.isArray(activities) && activities.map((activity) => {
          const coords = activity?.coordinates;
          if (!Array.isArray(coords) || coords.length < 2) return null;
          return (
            <Polyline
              key={activity.id}
              positions={coords}
              pathOptions={{
                color: resolveLineColor(activity?.type),
                opacity: resolveLineOpacity(activity?.type),
                weight: lineWeight,
              }}
            />
          );
        })}
        {/* Print-area outline. Drawn on the big preview only; thumbnails
            already fill the tile with the basemap so an outline at the
            edge would be invisible. Border-only — no fill — so the user
            sees the basemap underneath. */}
        {!compact && safeBounds && (
          <Rectangle
            bounds={safeBounds}
            pathOptions={{
              color: '#ff0040',
              weight: 2,
              opacity: 0.85,
              fillOpacity: 0,
              dashArray: '6 4',
            }}
          />
        )}
        {safeBounds && (
          <FitToBounds
            bounds={safeBounds}
            paddingPx={compact ? 4 : 30}
          />
        )}
      </MapContainer>
    </div>
  );
};

export default StylePreview;
